// Reusable multi-select user picker rendered as a SEPARATE modal on top of
// its host dialog. Every user card is uniform (Name highlighted, Emp Code +
// Department + Email in small text). Emits `onDone(selectedIds)` when the
// user confirms — the caller can then close it and update its parent form.
//
// Props:
//   open              boolean
//   onClose           () => void
//   onDone            (selectedIds: string[]) => void
//   users             array of directory users (id, name, employee_code, department, designation, email, role)
//   initialSelectedIds string[]  starting selection
//   title             optional dialog title
//   roleOptions       optional array of role keys — renders role chips at top of picker that auto-check/uncheck matching users
//
// Colour code (both light & dark):
//   Selected   → emerald glow (bg-emerald-50, border-emerald-500, ring emerald)
//   Unselected → rose glow    (bg-rose-50,    border-rose-400,    ring rose)

import { useEffect, useMemo, useState } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "./ui/dialog";
import { Badge } from "./ui/badge";
import { CheckCircle2, X, Search, Users } from "lucide-react";

const roleLabel = (r) => ({
  master_admin: "Master Admin", hr_admin: "HR Admin", sales_admin: "Sales Admin",
  sales_team: "Sales Team", accounts_admin: "Accounts Admin",
  dispatch_admin: "Dispatch Admin", it_admin: "IT Admin",
  crm_manager: "CRM Manager", documentation_team: "Documentation",
  trainer: "Trainer", employee: "Employee",
}[r] || r);

export default function UserPickerDialog({
  open,
  onClose,
  onDone,
  users = [],
  initialSelectedIds = [],
  title = "Select users",
  excludeIds = [],
  roleOptions = null,   // pass an array of role keys to enable the top role-chip strip
}) {
  const [selected, setSelected] = useState(new Set());
  const [selectedRoles, setSelectedRoles] = useState(new Set());
  const [q, setQ] = useState("");

  useEffect(() => {
    if (open) {
      setSelected(new Set(initialSelectedIds));
      setSelectedRoles(new Set());
      setQ("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const excludeSet = useMemo(() => new Set(excludeIds), [excludeIds]);
  const available = useMemo(
    () => users.filter((u) => !excludeSet.has(u.id)),
    [users, excludeSet]
  );
  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return available;
    return available.filter((u) =>
      (u.name || "").toLowerCase().includes(term) ||
      (u.email || "").toLowerCase().includes(term) ||
      (u.employee_code || "").toLowerCase().includes(term) ||
      (u.department || "").toLowerCase().includes(term) ||
      (u.designation || "").toLowerCase().includes(term) ||
      (u.role || "").toLowerCase().includes(term)
    );
  }, [available, q]);

  const toggle = (id) =>
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });

  // Toggle a role chip — auto-check / auto-uncheck every user in the
  // available list that has that role (helps for bulk audience selection).
  const toggleRole = (role) => {
    setSelectedRoles((prev) => {
      const next = new Set(prev);
      const on = !next.has(role);
      if (on) next.add(role); else next.delete(role);
      setSelected((sel) => {
        const nsel = new Set(sel);
        const roleIds = available.filter((u) => u.role === role).map((u) => u.id);
        if (on) roleIds.forEach((id) => nsel.add(id));
        else roleIds.forEach((id) => nsel.delete(id));
        return nsel;
      });
      return next;
    });
  };

  const selectAllFiltered = () =>
    setSelected((s) => new Set([...s, ...filtered.map((u) => u.id)]));

  const clearAll = () => setSelected(new Set());

  const done = () => {
    onDone(Array.from(selected));
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="max-w-4xl max-h-[92vh] overflow-hidden flex flex-col p-0"
        data-testid="user-picker-dialog"
      >
        <DialogHeader className="px-6 pt-5 pb-3 border-b border-slate-100 dark:border-slate-800 space-y-3">
          <DialogTitle className="flex items-center gap-2">
            <Users className="w-5 h-5 text-emerald-600" />
            {title}
            <Badge variant="secondary" className="ml-2 bg-emerald-50 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300">
              {selected.size} selected
            </Badge>
          </DialogTitle>
          {/* Roles/Teams filter — sits inside the banner so quick team
              filtering is the first thing the user sees. Auto-checks
              every user whose role matches the picked chip. */}
          {Array.isArray(roleOptions) && roleOptions.length > 0 && (
            <div data-testid="user-picker-roles-strip">
              <div className="text-[10.5px] uppercase tracking-wider font-bold text-slate-500 dark:text-slate-400 mb-1.5">
                Roles / Teams (auto-checks matching users)
              </div>
              <div className="flex flex-wrap gap-1.5">
                {roleOptions.map((r) => {
                  const on = selectedRoles.has(r);
                  return (
                    <button
                      key={r}
                      type="button"
                      onClick={() => toggleRole(r)}
                      className={`rounded-full px-3 py-1 text-[11.5px] font-semibold border transition-all ${
                        on
                          ? "bg-emerald-600 text-white border-emerald-600 shadow-sm hover:bg-emerald-700"
                          : "bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-300 border-slate-300 dark:border-slate-600 hover:border-emerald-400 hover:text-emerald-700"
                      }`}
                      data-testid={`user-picker-role-${r}`}
                    >
                      {roleLabel(r)}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </DialogHeader>

        <div className="px-6 pt-4 space-y-3">
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-slate-400" />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search by name, employee code, department, designation, email…"
                className="pl-8"
                autoFocus
                data-testid="user-picker-search"
              />
            </div>
            <button
              type="button"
              onClick={selectAllFiltered}
              className="text-[11.5px] px-2.5 py-1.5 rounded bg-emerald-50 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-700 hover:bg-emerald-100"
              data-testid="user-picker-select-all"
            >
              Select all ({filtered.length})
            </button>
            <button
              type="button"
              onClick={clearAll}
              className="text-[11.5px] px-2.5 py-1.5 rounded bg-slate-50 dark:bg-slate-800 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-700 hover:bg-slate-100"
              data-testid="user-picker-clear"
            >
              Clear
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-3">
          {filtered.length === 0 ? (
            <div className="rounded-xl border border-dashed border-slate-300 dark:border-slate-700 bg-white/60 dark:bg-slate-900/40 p-8 text-center text-slate-500 dark:text-slate-400 text-[13px]">
              No enrolled active users match.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
              {filtered.map((u) => {
                const isSel = selected.has(u.id);
                // Selected → emerald glow; Unselected → rose glow. Same in
                // dark mode using darker background variants.
                const wrapCls = isSel
                  ? "border-emerald-500 bg-emerald-50 dark:bg-emerald-950/40 shadow-[0_0_0_3px_rgba(16,185,129,0.18)] ring-1 ring-emerald-400/50"
                  : "border-rose-400 bg-rose-50 dark:bg-rose-950/40 shadow-[0_0_0_3px_rgba(244,63,94,0.15)] hover:shadow-[0_0_0_4px_rgba(244,63,94,0.22)]";
                const nameCls = isSel
                  ? "text-emerald-900 dark:text-emerald-200"
                  : "text-rose-800 dark:text-rose-300";
                const infoCls = isSel
                  ? "text-emerald-800/80 dark:text-emerald-300/80"
                  : "text-rose-700/80 dark:text-rose-300/80";
                return (
                  <div
                    key={u.id}
                    onClick={() => toggle(u.id)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => { if (e.key === " " || e.key === "Enter") { e.preventDefault(); toggle(u.id); } }}
                    className={`cursor-pointer select-none rounded-xl border-2 p-3 transition-all ${wrapCls}`}
                    data-testid={`user-picker-card-${u.id}`}
                  >
                    <div className="flex items-start gap-2">
                      <div className={`w-4 h-4 rounded-full mt-1 shrink-0 border-2 flex items-center justify-center ${isSel ? "bg-emerald-500 border-emerald-500" : "bg-white dark:bg-slate-900 border-rose-500"}`}>
                        {isSel && <CheckCircle2 className="w-3 h-3 text-white" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className={`text-[14px] font-semibold truncate ${nameCls}`}>
                          {u.name || u.email}
                        </div>
                        <div className={`mt-0.5 text-[10px] leading-snug space-y-0.5 ${infoCls}`}>
                          <div className="truncate"><span className="opacity-60">Dept:</span> {u.department || "—"}</div>
                          <div className="truncate"><span className="opacity-60">Emp Code:</span> <span className="font-mono">{u.employee_code || "—"}</span></div>
                          <div className="truncate"><span className="opacity-60">Email:</span> {u.email || "—"}</div>
                          <div className="truncate"><span className="opacity-60">Role:</span> {roleLabel(u.role)}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <DialogFooter className="px-6 py-3 border-t border-slate-100 dark:border-slate-800 gap-2">
          <Button variant="outline" onClick={onClose} data-testid="user-picker-cancel">
            <X className="w-4 h-4 mr-1.5" /> Cancel
          </Button>
          <Button
            onClick={done}
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="user-picker-done"
          >
            <CheckCircle2 className="w-4 h-4 mr-1.5" />
            Done · {selected.size} selected
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
